import { Link } from "react-router-dom";
import { Sparkles, Instagram, Facebook, Youtube } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import NewsletterForm from "@/components/NewsletterForm";
import GoogleTrustBadge from "@/components/GoogleTrustBadge";

export default function Footer() {
  const { data: seo } = useQuery({ queryKey: ["seo-settings"], queryFn: () => api.get("/seo/settings").then((r) => r.data), staleTime: 600000 });
  const socials = [
    { url: seo?.social_instagram, Icon: Instagram, label: "Instagram", testid: "footer-social-instagram" },
    { url: seo?.social_facebook, Icon: Facebook, label: "Facebook", testid: "footer-social-facebook" },
    { url: seo?.social_youtube, Icon: Youtube, label: "YouTube", testid: "footer-social-youtube" },
  ].filter((s) => s.url);
  return (
    <footer className="bg-[#144F75] text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-12">
        <div>
          <div className="mb-5">
            <img src="https://customer-assets.emergentagent.com/job_eyw-admin-suite/artifacts/3ze0y6kq_EnhancingYourWorld%20site%20logo%20with%20transparent%20background%20web%20version.png"
              alt="Enhancing Your World" className="h-20 w-auto" />
          </div>
          <p className="font-serif text-2xl text-white/90 max-w-md leading-snug">
            Enhancing Your World Enhances Our World.
          </p>
          <p className="text-white/60 text-sm mt-4 max-w-md">
            A conscious ecosystem for holistic recovery, community participation,
            stewardship, and human enhancement.
          </p>
          <div className="mt-6"><GoogleTrustBadge variant="dark" /></div>
          <div className="mt-6 text-sm text-white/80 space-y-1.5" data-testid="footer-contact">
            <p className="text-white/60 uppercase tracking-wide text-xs">Questions, comments, or concerns?</p>
            <p><a href="tel:+18599795505" className="hover:text-[#D98E73] font-medium" data-testid="footer-phone">859-979-5505</a></p>
            <p><a href="mailto:info@enhancingyourworld.com" className="hover:text-[#D98E73] font-medium" data-testid="footer-email">info@enhancingyourworld.com</a></p>
          </div>
          {socials.length > 0 && (
            <div className="flex items-center gap-3 mt-6" data-testid="footer-socials">
              {socials.map(({ url, Icon, label, testid }) => (
                <a key={label} href={url} target="_blank" rel="noreferrer" aria-label={label} data-testid={testid}
                  className="h-10 w-10 rounded-full bg-white/10 hover:bg-[#D98E73] flex items-center justify-center transition-colors">
                  <Icon size={18} />
                </a>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-x-8 gap-y-3 mt-8 text-sm text-white/70">
            <Link to="/gauge" className="hover:text-[#D98E73]">GAUGE Your Life</Link>
            <Link to="/help" className="hover:text-[#D98E73]">Help Center</Link>
            <Link to="/suggest" className="hover:text-[#D98E73]">Suggest an Improvement</Link>
            <Link to="/settings/profile" className="hover:text-[#D98E73]">Profile & Privacy</Link>
            <Link to="/schedule" className="hover:text-[#D98E73]">Events & Retreats</Link>
            <Link to="/articles" className="hover:text-[#D98E73]">Articles</Link>
            <Link to="/testimonials" className="hover:text-[#D98E73]">Testimonials</Link>
            <Link to="/agreements" className="hover:text-[#D98E73]">Member Agreements</Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#D98E73]">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-[#D98E73]">Terms of Service</Link>
            <Link to="/cancellation" data-testid="footer-cancellation-link" className="hover:text-[#D98E73]">Cancellation Policy</Link>
            <Link to="/refunds" data-testid="footer-refunds-link" className="hover:text-[#D98E73]">Refund Policy</Link>
            <Link to="/policies" className="hover:text-[#D98E73]">Site Policies & Privacy</Link>
            <Link to="/data-request" className="hover:text-[#D98E73]">Data Requests</Link>
            <Link to="/privacy-center" className="hover:text-[#D98E73]">Privacy Center</Link>
          </div>
        </div>
        <div className="lg:justify-self-end">
          <NewsletterForm dark />
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 text-white/50 text-xs flex flex-col sm:flex-row justify-between gap-2">
          <span>© {new Date().getFullYear()} Enhancing Your World Ecosystem</span>
          <span>Holistic Recovery · Community · Stewardship · Human Enhancement</span>
        </div>
      </div>
    </footer>
  );
}
